import React, { useState, useEffect } from 'react';
import {
  Lock,
  Monitor,
  Eye,
  EyeOff,
  MapPin,
  CheckCircle,
  Globe,
  Clock,
  Wifi,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { changePassword, getAuthSession, logoutSession } from '../../api/api';
import { useNavigate } from 'react-router-dom';
import { logoutThunk } from '../../redux/slices/authSlice';
import { clearDocument } from '../../redux/slices/documentSlice';
import { UAParser } from 'ua-parser-js';
import Loader2 from '../Loader/Loader2';
import { validatePassword } from '../../helpers/passwordPolicy';
import { handleApiError } from '../../helpers/handleApiError';

const SecurityTab = ({ isDarkMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const parseDevice = (uaString) => {
    if (!uaString) return 'Unknown Device';
    const parser = new UAParser(uaString);
    const browser = parser.getBrowser().name || 'Browser';
    const os = parser.getOS().name || 'OS';
    return `${browser} on ${os}`;
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      return toast.error('Please fill in all fields');
    }

    if (newPassword !== confirmPassword) {
      return toast.error('New passwords do not match');
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      return toast.error(validationError);
    }

    if (newPassword === oldPassword) {
      return toast.error('New password must be different from the current one');
    }

    try {
      setLoading(true);
      const res = await changePassword(user.id, oldPassword, newPassword);

      toast.success(
        res?.message || 'Password updated successfully. Please login again.'
      );

      setTimeout(() => {
        dispatch(logoutThunk());
        dispatch(clearDocument?.());
        navigate('/login');
      }, 2000);
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setSessionsLoading(true);
        const res = await getAuthSession();
        setSessions(res.sessions || []);
      } catch (err) {
        handleApiError(err, 'Failed to fetch sessions');
      } finally {
        setSessionsLoading(false);
      }
    };
    fetchSessions();
  }, []);
  const handleLogoutOtherSessions = async () => {
    try {
      if (!sessions || sessions.length === 0) {
        return toast.error('No sessions found.');
      }
      const currentSessionId = Math.max(...sessions.map((s) => s.id));
      const otherSessions = sessions.filter((s) => s.id !== currentSessionId);

      if (otherSessions.length === 0) {
        return toast.error('No other sessions to sign out.');
      }
      for (const s of otherSessions) {
        await logoutSession(s.id);
      }
      toast.success('Signed out all other sessions.');
      const res = await getAuthSession();
      setSessions(res.sessions || []);
    } catch (err) {
      toast.error(err.message || 'Failed to sign out sessions.');
    }
  };

  return (
    <div className="space-y-6 flex flex-col">
      {user?.provider !== 'google.com' && (
        <div
          className={`border rounded-lg p-6 shadow-sm ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-800'
          }`}
        >
          <div className="flex items-start mb-4">
            <div
              className={`p-2 rounded-md mr-3 flex items-center justify-center ${
                isDarkMode
                  ? 'bg-white/10 text-white'
                  : 'bg-indigo-100 text-indigo-500'
              }`}
            >
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <h3
                className={`font-semibold text-sm mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Change Password
              </h3>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Update your password regularly to maintain account security.
              </p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            {/* Old Password */}
            <div>
              <label className="block text-xs mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showOld ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className={`border rounded-md px-3 py-2 w-full pr-10 focus:outline-none focus:ring focus:border-indigo-300 ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowOld(!showOld)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showOld ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-xs mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`border rounded-md px-3 py-2 w-full pr-10 focus:outline-none focus:ring focus:border-indigo-300 ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-xs mb-1">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`border rounded-md px-3 py-2 w-full pr-10 focus:outline-none focus:ring focus:border-indigo-300 ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-500 text-white'
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          {/* Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleChangePassword}
              disabled={
                loading || !oldPassword || !newPassword || !confirmPassword
              }
              className={`px-4 py-2 rounded-md text-sm text-white transition ${
                loading || !oldPassword || !newPassword || !confirmPassword
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-brand hover:brightness-110'
              }`}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}
      {/* Two-Factor Authentication */}
      {/* <div
        className={`border rounded-lg p-6 shadow-sm ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-200 text-gray-800'
        }`}
      >
        <div className="flex items-start mb-4">
          <div
            className={`p-2 rounded-md mr-3 flex items-center justify-center ${
              isDarkMode
                ? 'bg-white/10 text-white'
                : 'bg-indigo-100 text-indigo-500'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
          </div>

          <div>
            <h3
              className={`font-semibold text-sm mb-1 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}
            >
              Two-Factor Authentication
            </h3>
            <p
              className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Add an extra layer of security to your account.
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div
            className={`text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            Status: <span className="font-medium text-red-500">Disabled</span>
          </div>
          <button className="px-4 py-2 rounded-md text-sm bg-brand text-white">
            Enable 2FA
          </button>
        </div>
      </div> */}

      {/* <div
        className={`border rounded-lg p-6 shadow-sm ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-200 text-gray-800'
        }`}
      >
        <div className="flex items-start mb-4">
          <div
            className={`p-2 rounded-md mr-3 flex items-center justify-center ${
              isDarkMode
                ? 'bg-white/10 text-white'
                : 'bg-indigo-100 text-indigo-500'
            }`}
          >
            <Activity className="w-4 h-4" />
          </div>

          <div>
            <h3
              className={`font-semibold text-sm mb-1 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}
            >
              Security Log
            </h3>
            <p
              className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              Review your recent account activity.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table
            className={`w-full text-xs text-left border rounded-md ${
              isDarkMode
                ? 'text-gray-300 border-gray-500'
                : 'text-gray-700 border-gray-200'
            }`}
          >
            <thead
              className={`uppercase text-[10px] ${
                isDarkMode
                  ? 'bg-gray-600 text-gray-300'
                  : 'bg-gray-50 text-gray-500'
              }`}
            >
              <tr>
                <th className="px-3 py-2">Activity</th>
                <th className="px-3 py-2">IP Address</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Date/Time</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  activity: 'Login Successful',
                  ip: '192.0.2.1',
                  location: 'San Francisco, CA',
                  time: 'Yesterday, 3:12 PM',
                },
                {
                  activity: 'Password Changed',
                  ip: '198.51.100.2',
                  location: 'New York, NY',
                  time: '2 days ago, 11:45 AM',
                },
                {
                  activity: 'Failed Login Attempt',
                  ip: '203.0.113.5',
                  location: 'Dallas, TX',
                  time: '3 days ago, 9:00 AM',
                  isError: true,
                },
              ].map((log, idx) => (
                <tr
                  key={idx}
                  className={`border-t ${
                    log.isError && !isDarkMode ? 'bg-red-50' : ''
                  }`}
                >
                  <td
                    className={`px-3 py-2 ${
                      log.isError ? 'text-red-600 font-medium' : ''
                    }`}
                  >
                    {log.activity}
                  </td>
                  <td className="px-3 py-2">{log.ip}</td>
                  <td className="px-3 py-2">{log.location}</td>
                  <td className="px-3 py-2">{log.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Active Sessions */}
      <div
        className={`border rounded-lg p-6 shadow-sm ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-200 text-gray-800'
        }`}
      >
        {/* Header with icon + text */}
        <div className="flex items-start mb-4">
          <div
            className={`p-2 rounded-md mr-3 flex items-center justify-center ${
              isDarkMode
                ? 'bg-white/10 text-white'
                : 'bg-indigo-100 text-indigo-500'
            }`}
          >
            <Monitor className="w-4 h-4" />
          </div>

          <div>
            <h3
              className={`font-semibold text-sm mb-1 ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}
            >
              Active Sessions
            </h3>
            <p
              className={`text-xs ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              View and manage your active sessions across devices.
            </p>
          </div>
        </div>

        <div className="text-sm space-y-3">
          {sessionsLoading ? (
            <Loader2 />
          ) : sessions.length === 0 ? (
            <p className="text-xs text-gray-500">No active sessions found.</p>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`flex justify-between items-center border p-3 rounded-md ${
                  isDarkMode ? 'border-gray-600' : 'bg-[#F9FAFB] border-none'
                }`}
              >
                <div className="flex gap-3">
                  {/* Device */}
                  <div className="mt-1 text-indigo-500">
                    <Monitor size={16} />
                  </div>

                  <div>
                    <p
                      className={`font-medium ${
                        isDarkMode ? 'text-white' : 'text-gray-800'
                      }`}
                    >
                      {parseDevice(session.device)}
                    </p>

                    <p
                      className={`text-xs flex flex-wrap gap-x-3 gap-y-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <Wifi size={12} />
                        {session.ip_address}
                      </span>

                      <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        {session.session_state}
                      </span>

                      <span className="flex items-center gap-1">
                        <Globe size={12} />
                        {session.country}
                      </span>

                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(session.login_time).toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Status */}
                {session.is_active && (
                  <span className="flex items-center gap-1 text-green-500 text-xs font-semibold">
                    <CheckCircle size={14} />
                    Active
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        {/* <div className="mt-4 flex justify-start">
          <button
            onClick={handleLogoutOtherSessions}
            disabled={sessions.filter((s) => !s.is_active).length === 0}
            className={`px-4 py-2 rounded-md text-sm font-medium transition shadow-sm
      ${
        sessions.filter((s) => !s.is_active).length === 0
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'bg-red-600 text-white hover:bg-red-700'
      }`}
          >
            Sign Out All Other Sessions
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default SecurityTab;
